import {
  html,
  attr,
  on,
  Signal,
  TNode,
  ForEach,
  Ensure,
  OnDispose,
} from '@tempots/dom'
import { Anchor } from '@tempots/ui'
import { Icon } from '@tempots/beatui'
import { Logo, NavLink } from '@/components/ui'
import { OrgProjectDisplayer } from '@/components/org-project-displayer/org-project-displayer'
import {
  navSections,
  startJobsCountPolling,
  startRealtimeJobUpdates,
  stopRealtimeJobUpdates,
} from '@/state/navigation'
import { Workspace } from '@/state/workspace'

export interface SidebarOptions {
  opened: Signal<boolean>
  setOpen: (value: boolean) => void
}

export function Sidebar(
  { opened, setOpen }: SidebarOptions,
  ...children: TNode[]
) {
  // Track cleanup functions for job updates
  let currentCleanup: (() => void) | null = null

  // Start real-time job updates (uses WebSocket when project selected, falls back to polling)
  function updateJobsSubscription(projectId: string | null): void {
    // Clean up previous subscription
    if (currentCleanup) {
      currentCleanup()
      currentCleanup = null
    }

    if (projectId) {
      // Use WebSocket for real-time updates when project is selected
      currentCleanup = startRealtimeJobUpdates(projectId)
    } else {
      // Fall back to polling when no project selected
      currentCleanup = startJobsCountPolling()
    }
  }

  // Initialize with current project
  Workspace.projectId.on((projectId) => {
    updateJobsSubscription(projectId)
  })

  OnDispose(() => {
    stopRealtimeJobUpdates()
    if (currentCleanup) {
      currentCleanup()
    }
  })

  return html.aside(
    attr.id('sidebar'),
    attr.class(
      opened.map(
        (isOpen) =>
          `fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 flex flex-col transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:z-auto ${
            isOpen ? 'translate-x-0' : '-translate-x-full'
          }`
      )
    ),
    // Header
    html.div(
      attr.class(
        'h-16 flex items-center justify-between px-6 border-b border-gray-200'
      ),
      Anchor(
        {
          href: Workspace.projectId.map((pid) =>
            pid != null ? `/p/${pid}` : '#'
          ),
        },
        attr.class('flex items-center gap-2'),
        Logo({ displaySlogan: true, size: 'sm' })
      ),
      html.button(
        on.click(() => setOpen(false)),
        attr.class('lg:hidden p-2 rounded-lg hover:bg-gray-100'),
        Icon({ icon: 'mdi:close' }, attr.class('w-6 h-6 text-gray-500'))
      )
    ),
    // Organization/Project Switcher
    html.div(
      attr.class('px-3 py-4 border-b border-gray-200'),
      OrgProjectDisplayer()
    ),
    // Navigation
    html.nav(
      attr.class('flex-1 px-3 py-4 space-y-6 overflow-y-auto'),
      ForEach(navSections, (section) =>
        html.div(
          Ensure(
            section.map((s) => (s.title !== '' ? s.title : null)),
            (title) =>
              html.h3(
                attr.class(
                  'px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2'
                ),
                title
              )
          ),
          html.div(
            attr.class('space-y-1'),
            ForEach(
              section.map((s) => s.items),
              (item) =>
                NavLink({
                  href: item.map((i) => i.href),
                  iconName: item.map((i) => i.iconName),
                  label: item.map((i) => i.label),
                  badge: item.map((i) => i.badge),
                })
            )
          )
        )
      ),
      // Additional children if any
      ...children
    )
  )
}
